import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { NgxUiLoaderService } from 'ngx-ui-loader';
import { ToastService } from '../../../../shared/services/toast.service';
import { OrganizationService } from '../../services/organization.service';
import { Organization } from '../../models/organization';
import { OrgLevelAdminService } from '../../services/orgleveladmin.service';
import { FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { LaunchDarklyService } from 'src/app/shared/services/launch-darkly.service';
import { EditOrganizationDialogComponent } from '../edit-organization-dialog/edit-organization-dialog.component';
import { EditSiteDialogComponent } from '../edit-site-dialog/edit-site-dialog.component';
import { MoveSiteDialogComponent } from '../move-site-dialog/move-site-dialog.component';
import { SiteMoveService } from '../../services/site-move.service';
import { AuthService } from 'src/app/auth/auth.service';
import { Role } from 'src/app/shared/models/auth/role';
import { Subscription } from 'rxjs';
import * as constants from '../../../../config/app-constants';

interface EditCustomerChildRoute {
  title: string;
  routerLink: string;
  idValue: string;
  menuType: MenuType;
  launchDarklyFlag?: string;
}

enum MenuType {
  ParentAccount,
  Site,
  Both
}

const editCustomerChildRoutes: EditCustomerChildRoute[] = [{
  title: 'Onboarding',
  routerLink: 'onboarding',
  idValue: 'OrgOnboardingTab',
  menuType: MenuType.Site
}, {
  title: 'Permission Groups',
  routerLink: 'permission-groups',
  idValue: 'PermissionGroupsTab',
  menuType: MenuType.ParentAccount
}, {
  title: 'User Mgmt & Permissions',
  routerLink: 'user-mgmt-permissions',
  idValue: 'OrgUserManagementTab',
  menuType: MenuType.Both
}, {
  title: 'Incada Information',
  routerLink: 'incada-information',
  idValue: 'IncadaInformationTab',
  menuType: MenuType.Both,
  launchDarklyFlag: 'release-incada'
}];

@Component({
  selector: 'app-edit-customer',
  templateUrl: './edit-customer.component.html',
  styleUrls: ['./edit-customer.component.scss'],
  standalone: false
})
export class EditCustomerComponent implements OnInit, OnDestroy {

  public currentOrg: Organization;
  public activeOrgs: Organization[] = [];
  public visibleChildren: EditCustomerChildRoute[] = [];
  public SelectedOrgName: string;
  public activeChild: string;
  public activeId: string;
  public showEditOrganization = false;
  public canMoveSite = false;
  private profileSubscription: Subscription;
  private incadaFlagEnabled = false;
  private flagSubscription: Subscription;
  public fb: FormBuilder = new FormBuilder();
  public editNameFormGroup: FormGroup = this.fb.group({
    siteName: new FormControl(''),
    editing: new FormControl(false),
    editable: new FormControl(false)
  });

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private ngxLoader: NgxUiLoaderService,
    private organizationService: OrganizationService,
    private toastService: ToastService,
    public orgLevelAdminService: OrgLevelAdminService,
    private dialog: MatDialog,
    private readonly launchDarklyService: LaunchDarklyService,
    private readonly auth: AuthService,
    private readonly siteMoves: SiteMoveService
  ) { }

  ngOnInit() {
    this.profileSubscription = this.auth.profile$.subscribe(profile => {
      this.canMoveSite = false;
      if (profile.hasAnyRoleOf(Role.admin, Role.parataDev)) {
        this.siteMoves.access('write').subscribe({
          next: access => { this.canMoveSite = access?.allowed === true; },
          error: () => { this.canMoveSite = false; },
        });
      }
    });
    this.activeId = this.route?.snapshot?.params?.id;
    this.activeChild = this.route?.snapshot?.firstChild?.routeConfig?.path;
    if (!this.activeId)
      this.toastService.openToast('Invalid Id!', constants.ToastPanelType.warning);

    this.organizationService.getActiveOrganizations().subscribe({
      next: (orgs => {
        if (!orgs.some((t) => t?.organizationId === this.activeId)) {
          this.toastService.openToast('Organization not found!', constants.ToastPanelType.warning);
          return;
        }
        this.activeOrgs = orgs;
        this.currentOrg = orgs.filter((t) => t?.organizationId === this.activeId)[0];
        const org = orgs.filter((t) => t?.organizationId === this.currentOrg.parent_Account_Id);
        this.SelectedOrgName = org[0]?.name;

        this.editNameFormGroup.get('editable').setValue(!this.currentOrg.is_Parent_Account);
        this.editNameFormGroup.get('siteName').setValue(this.currentOrg.name);

        this.orgLevelAdminService.setOrgLevelRowValue(this.currentOrg.is_Parent_Account);

        this.flagSubscription = this.launchDarklyService.flagChange.subscribe((flags) => {
          this.incadaFlagEnabled = flags['release-incada'] ?? false;
          this.evaluateEditOrgVisibility();

          // Filter visibleChildren based on menuType and launchDarklyFlag
          this.visibleChildren = editCustomerChildRoutes.filter(x => {
            // Filter by menuType
            const menuTypeAllowed = this.currentOrg.is_Parent_Account
              ? (x.menuType === MenuType.ParentAccount || x.menuType === MenuType.Both)
              : (x.menuType === MenuType.Site || x.menuType === MenuType.Both);
            // If a launchDarklyFlag is set, require it to be enabled
            if (x.launchDarklyFlag) {
              return menuTypeAllowed && (flags[x.launchDarklyFlag] ?? false);
            }
            return menuTypeAllowed;
          });
        });
      }),
      error: () => {
        this.toastService.openToast('Failed to load organizations!', constants.ToastPanelType.warning);
      }
    });
  }

  ngOnDestroy() {
    if (this.flagSubscription) {
      this.flagSubscription.unsubscribe();
    }
    this.profileSubscription?.unsubscribe();
  }

  evaluateEditOrgVisibility() {
    this.showEditOrganization = this.incadaFlagEnabled
      && !!this.currentOrg?.is_Parent_Account;
  }

  openEditOrganizationDialog() {
    const dialogRef = this.dialog.open(EditOrganizationDialogComponent, {
      backdropClass: ['smDialog'],
      disableClose: true,
      data: { organization: this.currentOrg }
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result === 'success') {
        // Reload org to pick up updated fields
        this.organizationService.getByID(this.activeId).subscribe((org: Organization) => {
          this.currentOrg = org;
          this.evaluateEditOrgVisibility();
          this.refreshChildRoute();
        });
      }
    });
  }

  editSite() {
    if (this.incadaFlagEnabled) {
      const parentOrg = this.activeOrgs.find(o => o.organizationId === this.currentOrg.parent_Account_Id) || null;

      const dialogRef = this.dialog.open(EditSiteDialogComponent, {
        width: '50vw',
        maxWidth: '50vw',
        disableClose: true,
        data: { organization: this.currentOrg, parentOrg }
      });

      dialogRef.afterClosed().subscribe((result) => {
        if (result === 'success') {
          // Reload org to pick up updated fields
          this.organizationService.getByID(this.activeId).subscribe((org: Organization) => {
            this.currentOrg = org;
            this.refreshChildRoute();
          });
        }
      });
    } else {
      this.editNameFormGroup.get('editing').setValue(true);
    }
  }

  openMoveSiteDialog() {
    if (!this.canMoveSite || !this.currentOrg || this.currentOrg.is_Parent_Account) {
      return;
    }

    const dialogRef = this.dialog.open(MoveSiteDialogComponent, {
      width: 'min(1120px, calc(100vw - 32px))',
      maxWidth: 'calc(100vw - 32px)',
      height: 'min(800px, calc(100vh - 32px))',
      maxHeight: 'calc(100vh - 32px)',
      disableClose: true,
      autoFocus: 'dialog',
      data: {
        organization: this.currentOrg,
        parentOrganizations: this.activeOrgs.filter(org => org.is_Parent_Account),
      },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result === 'success') {
        this.organizationService.getByID(this.activeId).subscribe((org: Organization) => {
          this.currentOrg = org;
          this.refreshChildRoute();
        });
      }
    });
  }

  siteNameExists(name) {
    return this.activeOrgs.some(org =>
      org.parent_Account_Id === this.currentOrg.parent_Account_Id &&
      org.name.localeCompare(name.toLowerCase(), undefined, { sensitivity: 'base' }) === 0
    );
  }

  saveSiteName() {
    if (this.editNameFormGroup.get('siteName').invalid) {
      this.toastService.openToast('Invalid site name!', constants.ToastPanelType.warning);
      return;
    }

    if (this.editNameFormGroup.get('siteName').value == this.currentOrg.name) {
      this.toastService.openToast('Update site name!', constants.ToastPanelType.warning);
      return;
    }

    if (this.siteNameExists(this.editNameFormGroup.get('siteName').value)) {
      this.toastService.openToast('Site name already exists!', constants.ToastPanelType.warning);
      return;
    }

    this.ngxLoader.start('update');
    this.organizationService.updateSite(this.currentOrg?.parent_Account_Id, this.currentOrg?.organizationId, {
      name: this.editNameFormGroup.get('siteName').value
    })
      .subscribe({
        next: () => {
          this.toastService.openToast('Site Updated!', constants.ToastPanelType.done);
          this.currentOrg.name = this.editNameFormGroup.get('siteName').value;
          this.clearSiteName();
        },
        error: (response) => {
          this.toastService.stopLoadingAndShowError(response, 'Error while saving site!', 'update');
        },
        complete: () => {
          this.ngxLoader.stop('update');
        }
      });
  }

  clearSiteName() {
    this.editNameFormGroup.get('editing').setValue(false);
    this.editNameFormGroup.get('siteName').setValue(this.currentOrg.name);
  }

  /**
   * Forces the active child route to re-initialize by navigating away and back,
   * causing routed child components (visibleChildren panels) to reload their data.
   */
  private refreshChildRoute() {
    const currentUrl = this.router.url;
    this.router.navigateByUrl('/', { skipLocationChange: true }).then(() => {
      this.router.navigateByUrl(currentUrl);
    });
  }
}
